/**
 * Utility for managing filter usage tracking in localStorage.
 * Tracks when filters were last used to support recent filter sorting.
 */

const FILTER_USAGE_KEY_PREFIX = 'filter_usage_'
const RECENT_FILTER_DAYS = 7

export interface FilterUsageRecord {
  [filterId: string]: number // timestamp
}

/**
 * Centralized localStorage management for filter usage tracking
 */
export const FilterUsageStorage = {
  /**
   * Generate localStorage key for a given entity
   */
  getKey: (entityName: string): string => {
    return `${FILTER_USAGE_KEY_PREFIX}${entityName}`
  },

  /**
   * Load filter usage data from localStorage
   */
  load: (entityName: string): FilterUsageRecord => {
    try {
      const key = FilterUsageStorage.getKey(entityName)
      const data = localStorage.getItem(key)
      return data ? JSON.parse(data) : {}
    } catch (e) {
      console.error('Failed to load filter usage from localStorage:', e)
      return {}
    }
  },

  /**
   * Save filter usage timestamp to localStorage
   */
  save: (
    entityName: string,
    filterId: string,
    timestamp: number = Date.now(),
  ): void => {
    try {
      const usage = FilterUsageStorage.load(entityName)
      usage[filterId] = timestamp
      const key = FilterUsageStorage.getKey(entityName)
      localStorage.setItem(key, JSON.stringify(usage))
    } catch (e) {
      console.error('Failed to save filter usage to localStorage:', e)
    }
  },

  /**
   * Check if a filter was used within the recent timeframe
   */
  isRecent: (lastUsed: number | undefined): boolean => {
    if (!lastUsed) return false
    const daysAgo = (Date.now() - lastUsed) / (1000 * 60 * 60 * 24)
    return daysAgo <= RECENT_FILTER_DAYS
  },

  /**
   * Clear all filter usage data for an entity
   */
  clear: (entityName: string): void => {
    try {
      const key = FilterUsageStorage.getKey(entityName)
      localStorage.removeItem(key)
    } catch (e) {
      console.error('Failed to clear filter usage from localStorage:', e)
    }
  },

  /**
   * Get the last used timestamp for a specific filter
   */
  getLastUsed: (entityName: string, filterId: string): number | undefined => {
    const usage = FilterUsageStorage.load(entityName)
    return usage[filterId]
  },
}
